import type { PlpRoot } from '@core/Entities/Search/PlpRoot.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import type {
  CollapsibleInterlinkingItem,
  CollapsibleInterlinkingProps,
} from '@page-builder/Components/InterLinking/CollapsibleInterLinking/CollapsibleInterlinking.entity';

import type {
  ExtendedAggregations,
  SectionConfig,
  SectionOption,
  VehicleInterlinkingClientProps,
} from '../VehicleInterlinking.entities';
import {
  CARRY_OVER_FILTERS,
  MAKE_INTERLINKING_SECTIONS,
  MAKE_MODEL_INTERLINKING_SECTIONS,
  PLP_VEHICLE_INTERLINKING_SECTIONS,
} from '../VehicleInterlinkingSectionsConfigs';

export const getAllOptions = (
  options: SectionOption[],
  isPricesSection = false,
): SectionOption[] => {
  const filteredOptions = options.filter(option => option.count > 0);

  if (isPricesSection) {
    return filteredOptions;
  }

  return filteredOptions.sort((a, b) => b.count - a.count);
};

const createLinkBlock = (
  config: SectionConfig,
  options: SectionOption[],
  messages: Record<string, string>,
  make: string | undefined,
  modelWithMake: string | undefined,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
  root: PlpRoot,
  combinedParams: FilterParams | undefined,
  activeFilterDisplayName: string | undefined,
): CollapsibleInterlinkingItem['linkBlocks'][number] | null => {
  const isPricesSection = config.key === 'prices';
  const allOptions = getAllOptions(options, isPricesSection);

  if (allOptions.length === 0) return null;

  const cta = allOptions.map(option => {
    const displayName = config.getDisplayName(option, messages);
    const filters = config.createFilters(make, modelWithMake, option.id, combinedParams);

    return {
      ctaText: config.formatCtaText(
        makeDisplayName,
        modelDisplayName,
        displayName,
        activeFilterDisplayName,
      ),
      link: createPathFromFilters(filters, root),
    };
  });
  return {
    heading: messages[config.headingKey],
    // Temporary fix to avoid breaking changes to the PLP.spec tests
    dataTestId:
      config.key === 'models'
        ? 'plp-all-models'
        : config.key === 'makes'
          ? 'plp-all-makes'
          : `${config.key}-interlinking-section`,
    cta,
  };
};

const getActiveFilterDisplayName = (
  combinedParams: FilterParams | undefined,
  messages: Record<string, string>,
): string | undefined => {
  const activeFilter = Object.entries(combinedParams ?? {}).find(
    ([key, value]) => (CARRY_OVER_FILTERS as readonly string[]).includes(key) && Boolean(value),
  );
  const activeFilterValue = Array.isArray(activeFilter?.[1])
    ? activeFilter[1][0]
    : activeFilter?.[1];

  return activeFilterValue
    ? messages[activeFilterValue] || messages[`${activeFilterValue}.one`] || activeFilterValue
    : undefined;
};

type TransformToInterlinkingContentProps = VehicleInterlinkingClientProps & {
  aggregations: ExtendedAggregations;
};

export const transformToInterlinkingContent = ({
  aggregations,
  serverAggregations,
  messages,
  root,
  combinedParams,
}: TransformToInterlinkingContentProps): CollapsibleInterlinkingProps => {
  const make = combinedParams?.make?.[0];
  const modelWithMake = combinedParams?.model?.[0];
  const model = modelWithMake ? modelWithMake.split('-')[1] : undefined;
  const makeDisplayName = (make && serverAggregations.make?.[make]?.label) || make || '';
  const modelDisplayName =
    modelWithMake && make
      ? serverAggregations.model?.[make]?.models[modelWithMake]?.label || model
      : undefined;
  const configs = modelWithMake
    ? MAKE_MODEL_INTERLINKING_SECTIONS
    : make
      ? MAKE_INTERLINKING_SECTIONS
      : PLP_VEHICLE_INTERLINKING_SECTIONS;
  const activeFilterDisplayName = getActiveFilterDisplayName(combinedParams, messages);
  const linkBlocks: CollapsibleInterlinkingItem['linkBlocks'] = configs
    .map(config =>
      createLinkBlock(
        config,
        config.getOptions(aggregations, make, modelWithMake, combinedParams),
        messages,
        make,
        modelWithMake,
        makeDisplayName,
        modelDisplayName,
        root,
        combinedParams,
        activeFilterDisplayName,
      ),
    )
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    interlinkingItems: [
      {
        heading: make ? messages.moreOptions : messages.moreUsedCarOptions,
        linkBlocks,
      },
    ],
  };
};
